import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import User from 'types/User';
import { MembershipTier } from 'types/SignUpData';
import { TIER_CONFIG, TIER_OPTIONS } from 'components/project/TierChip';
import UserHistoryPanel from 'components/auth/UserHistoryPanel';

interface EditUserDialogProps {
  open: boolean;
  user: User | null;
  tier: MembershipTier;
  adminComment: string;
  savingEmail: string | null;
  onClose: () => void;
  onTierChange: (tier: MembershipTier) => void;
  onAdminCommentChange: (comment: string) => void;
  onSave: () => void;
  onEnableAccess: () => void;
  onRestrictAccess: () => void;
}

const EditUserDialog: React.FC<EditUserDialogProps> = ({
  open,
  user,
  tier,
  adminComment,
  savingEmail,
  onClose,
  onTierChange,
  onAdminCommentChange,
  onSave,
  onEnableAccess,
  onRestrictAccess,
}) => {
  const isDenied = user?.approved === false;
  const isApproved = user?.approved === true;
  const isSaving = savingEmail === user?.email;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Edit User</DialogTitle>
      <DialogContent>
        {user && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ wordBreak: 'break-word' }}>
              <Typography variant="body2" color="text.secondary">
                User
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {user.firstName} {user.lastName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user.email}
              </Typography>
            </Box>

            {/* Denied-access notice with re-enable action. Emits an
                ACCOUNT_ENABLED history event [1]. */}
            {isDenied && (
              <Alert
                severity="warning"
                sx={{ alignItems: 'center', '& .MuiAlert-message': { display: 'flex', alignItems: 'center' }, '& .MuiAlert-action': { paddingTop: 0 } }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={onEnableAccess}
                    disabled={isSaving}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Enable Access
                  </Button>
                }
              >
                This user's access was denied. You can re-enable their account here.
              </Alert>
            )}

            {/* Approved-user notice with restrict action. */}
            {isApproved && (
              <Alert
                severity="info"
                sx={{ alignItems: 'center', '& .MuiAlert-message': { display: 'flex', alignItems: 'center' }, '& .MuiAlert-action': { paddingTop: 0 } }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={onRestrictAccess}
                    disabled={isSaving}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Disable Access
                  </Button>
                }
              >
                This user currently has access to MSD-LIVE. Disable their account here.
              </Alert>
            )}

            <Divider />

            <FormControl fullWidth>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Membership Tier
              </Typography>
              <Select
                value={tier}
                onChange={(e) => onTierChange(e.target.value as MembershipTier)}
                MenuProps={{ sx: { zIndex: 1500 } }}
              >
                {TIER_OPTIONS.map((t) => (
                  <MenuItem key={t} value={t}>
                    {TIER_CONFIG[t].label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Admin Comment (optional)"
              placeholder="Add a comment…"
              value={adminComment}
              onChange={(e) => onAdminCommentChange(e.target.value)}
              multiline
              rows={3}
              fullWidth
            />

            <UserHistoryPanel email={user.email} />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isSaving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={onSave}
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {isSaving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditUserDialog;